import { ExternalLink, LogIn, ShieldCheck } from 'lucide-react'
import { demoAccounts, superadmin, type DemoAccount } from '../demoAccounts'

/**
 * One-click sign-in, for demos and local development only.
 *
 * Renders nothing when `VITE_DEMO_ACCOUNTS` is unset — and because the flag is
 * read through `import.meta.env`, the credentials are dropped from the bundle
 * entirely rather than merely hidden. A production build does not contain
 * them.
 */
export function DemoAccounts({
  onPick,
  disabled,
}: {
  onPick: (account: DemoAccount) => void
  disabled?: boolean
}) {
  if (demoAccounts.length === 0) return null

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium uppercase tracking-[0.06em] text-ink-muted">
          Demo accounts
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {demoAccounts.map((account) => (
          <button
            key={account.email}
            type="button"
            disabled={disabled}
            onClick={() => onPick(account)}
            className="group flex items-center gap-3 rounded-lg p-3 text-left ring-1 ring-border-strong transition-colors hover:bg-sunken disabled:pointer-events-none disabled:opacity-50"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand">
              <LogIn className="h-4 w-4" strokeWidth={2} aria-hidden />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-ink">{account.label}</span>
              <span className="block truncate text-xs text-ink-muted">{account.description}</span>
            </span>

            <span className="shrink-0 text-xs font-medium text-brand opacity-0 transition-opacity group-hover:opacity-100">
              Sign in
            </span>
          </button>
        ))}
      </div>

      {/*
        A LINK, not a button, and visually set apart from the two above.

        The superadmin cannot sign in here at all: platform admins live in
        their own table behind their own guard, which is what lets every
        `users.tenant_id` be NOT NULL with no exceptions. Filling this form
        with those credentials would post something the SPA can never accept.
      */}
      <a
        href={superadmin.url}
        target="_blank"
        rel="noreferrer"
        className="group mt-2 flex items-center gap-3 rounded-lg border border-dashed border-border-strong p-3 text-left transition-colors hover:bg-sunken"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sunken text-ink-secondary">
          <ShieldCheck className="h-4 w-4" strokeWidth={2} aria-hidden />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-ink">{superadmin.label}</span>
          <span className="block truncate text-xs text-ink-muted">{superadmin.description}</span>
        </span>

        <ExternalLink
          className="h-4 w-4 shrink-0 text-ink-muted transition-colors group-hover:text-brand"
          strokeWidth={2}
          aria-hidden
        />
      </a>

      <p className="tabular mt-2 text-center text-xs text-ink-muted">
        {superadmin.email} · {superadmin.password}
      </p>

      <p className="mt-3 text-center text-xs text-ink-muted">
        Sample data. Anything you change here is fair game.
      </p>
    </div>
  )
}
